import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { PlayArrow as StartIcon } from '@mui/icons-material';
import type { Appointment } from '@app-types/reception';

interface StartConsultationModalProps {
  open: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onConfirm: (appointmentId: string) => Promise<void>;
}

export const StartConsultationModal: React.FC<StartConsultationModalProps> = ({
  open,
  appointment,
  onClose,
  onConfirm
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!appointment) return;

    setLoading(true);
    setError(null);

    try {
      await onConfirm(appointment.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Eroare la pornirea consultatiei');
    } finally {
      setLoading(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <StartIcon color="warning" />
          Start consultatie
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          Confirmati inceperea consultatiei pentru:
        </Typography>

        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mt: 2 }}>
          <Typography variant="body2">
            <strong>Animal:</strong> {appointment.animal?.name || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Proprietar:</strong>{' '}
            {appointment.owner
              ? `${appointment.owner.firstName} ${appointment.owner.lastName}`
              : 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Medic:</strong>{' '}
            {appointment.doctor
              ? `Dr. ${appointment.doctor.firstName} ${appointment.doctor.lastName}`
              : 'N/A'}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          Statusul va fi actualizat in dashboard-ul medicului.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Anuleaza
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="warning"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <StartIcon />}
        >
          {loading ? 'Se proceseaza...' : 'Start consultatie'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

